import { useCallback, useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckoutContext } from '@/context/CheckoutContext';
import { CHECKOUT_DATA } from '@/constants/queries';
import { SESSION_ID } from '@/constants/params';
import {
  APPLE_PAY_TAB_DATA_MESSAGE,
  APPLE_PAY_TAB_READY_MESSAGE,
  APPLE_PAY_TAB_PAYMENT_AUTHORIZED_MESSAGE,
  buildApplePayTabUrl,
  type ApplePayTabPaymentAuthorizedMessage,
} from '@/utils/applePayTabFallback';
import { logError, logInfo } from '@/utils/logger';
import { useGetOrder } from '@/api/order';
import { ChargeStates, PaymentStates } from '@/types/order';
import { PaymentMethodEnum } from '@/types/common';
import type { BasePaymentResponse } from '@/types/payment';
import { getProviderFromPaymentMethod } from '@/utils/paymentParams';
import { mixpanelService } from '@/services/mixpanelService';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { toAppliedPromoCodeData } from '@/utils/promoCode';

export const TAB_CLOSED_POLL_INTERVAL_MS = 500;
/**
 * A tab that pays posts its result and closes in the same breath, and the two
 * reach this window through different task sources — a `message` task and the
 * timer below — which the HTML spec does not order against each other. So the
 * tab can be seen closed while its handoff is still queued, and tearing the
 * listener down on that first sighting would drop a payment the shopper has
 * already been charged for. Let an in-flight handoff land before treating a
 * closed tab as abandoned: half a second of overlay on the abandonment path
 * is a cheap premium against restoring a payable checkout over a paid order.
 */
export const HANDOFF_GRACE_AFTER_CLOSE_MS = 500;

/**
 * States that prove the tab got far enough that this frame must resume the
 * post-payment flow (polling / success) instead of restoring a payable
 * checkout. `charge_pending` / `charge_succeed` / `payment_succeed` mean
 * money moved or is moving.
 *
 * Deliberately EXCLUDES `charge_failed` / `payment_failed`: a declined
 * attempt leaves the session's orderId in a failed terminal state, and
 * treating that as "committed" used to call `resumeApplePayTabPayment` →
 * success UI → order-poll FAILED → fatal `showBoundary`, which tore down
 * the opener checkout right after the shopper's bad pay. Failed attempts
 * restore the payable checkout so the shopper can retry.
 */
const ORDER_STATES_WITH_A_COMMITTED_PAYMENT = new Set<ChargeStates | PaymentStates>([
  ChargeStates.PENDING,
  ChargeStates.SUCCESS,
  PaymentStates.SUCCESS,
]);

const ORDER_STATES_FAILED_ATTEMPT = new Set<ChargeStates | PaymentStates>([
  ChargeStates.FAILED,
  PaymentStates.FAILED,
]);

/**
 * Opener side of the iOS <= 16 Apple Pay new-tab fallback.
 *
 * `openApplePayTab` must be called from inside the click handler: the
 * `window.open` is issued synchronously so Safari attributes it to the
 * user gesture (same rule as `handleOpenTab` for redirect APMs).
 *
 * After the tab opens, this hook:
 *  - answers the tab's `apple-pay-tab:ready` postMessage with the cached
 *    `/checkout/v3` payload (storage is partitioned for cross-origin
 *    iframes in Safari, so a direct window-reference postMessage is the
 *    only reliable same-origin handoff channel);
 *  - shows the redirect-style processing overlay while the tab is open;
 *  - takes the order over on `apple-pay-tab:payment-authorized`: the tab
 *    hosts the Apple Pay sheet and closes the moment it authorizes, so
 *    polling, the success screen and the SDK events all run here;
 *  - polls `tab.closed` and, for a tab that closed without handing anything
 *    back, waits out `HANDOFF_GRACE_AFTER_CLOSE_MS` for a message still in
 *    flight and then checks the order before restoring the pre-click state.
 */
export const useApplePayTabHandoff = () => {
  const context = useContext(CheckoutContext);
  const queryClient = useQueryClient();
  const { mutateAsync: getOrder } = useGetOrder();
  const getOrderRef = useRef(getOrder);
  getOrderRef.current = getOrder;
  const tabRef = useRef<Window | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const paidInTabRef = useRef(false);
  // Fresh context for late callbacks (poll tick / message reply) without
  // rebinding openApplePayTab on every render.
  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(
    () => () => {
      cleanupRef.current?.();
    },
    []
  );

  const openApplePayTab = useCallback((): boolean => {
    if (tabRef.current && !tabRef.current.closed) {
      tabRef.current.focus();
      return true;
    }

    // Reopening after the previous tab closed but before the poll tick
    // noticed: tear the old session down now, otherwise its message
    // listener and interval would stack onto (and answer for) the new tab.
    cleanupRef.current?.();

    const sessionToken =
      new URLSearchParams(window.location.search).get(SESSION_ID) || contextRef.current.sessionId || '';
    // Synchronous window.open inside the click gesture — nothing may await
    // before this line or Safari's popup blocker eats the tab.
    const tab = window.open(buildApplePayTabUrl(sessionToken), '_blank');
    if (!tab) {
      logError('Apple Pay tab fallback: window.open returned null (popup blocked)');
      mixpanelService.track(MixpanelEvents.APPLE_PAY_TAB_FALLBACK_CLICKED, {
        ...(getMixpanelProperties(contextRef.current) as Record<string, unknown>),
        tab_opened: false,
      });
      return false;
    }

    tabRef.current = tab;
    paidInTabRef.current = false;
    contextRef.current.actions.setIsExpressPaymentInProcess(true);
    contextRef.current.actions.setApplePayTabOpen(true);
    mixpanelService.track(MixpanelEvents.APPLE_PAY_TAB_FALLBACK_CLICKED, {
      ...(getMixpanelProperties(contextRef.current) as Record<string, unknown>),
      tab_opened: true,
    });

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      // Only the tab THIS click opened may receive the payload — any other
      // same-origin window (nested iframe, another popup) is ignored.
      if (!event.source || event.source !== tabRef.current) return;
      const messageType = (event.data as { type?: string } | null)?.type;
      if (messageType === APPLE_PAY_TAB_PAYMENT_AUTHORIZED_MESSAGE) {
        handleTabPaymentAuthorized(event.data as ApplePayTabPaymentAuthorizedMessage);
        return;
      }
      if (messageType !== APPLE_PAY_TAB_READY_MESSAGE) return;
      const cached = queryClient.getQueryData([CHECKOUT_DATA]) as Record<string, unknown> | undefined;
      // Promo / ZIP-tax adjustments live only in checkout context
      // (`updatePriceBreakdown` mutates `checkout.pricing`, never the query
      // cache), so overlay the live pricing on the cached payload. The ZIP
      // that produced that tax rides along for the same reason.
      const { pricing, promoCodeBreakdown, addressDetails } = contextRef.current;
      const checkoutData = cached && pricing ? { ...cached, pricing } : cached;
      (event.source as Window).postMessage(
        {
          type: APPLE_PAY_TAB_DATA_MESSAGE,
          checkoutData,
          promoCode: toAppliedPromoCodeData(promoCodeBreakdown),
          addressDetails: addressDetails ?? null,
        },
        window.location.origin
      );
      logInfo('Apple Pay tab fallback: checkout data handed off to tab');
    };
    window.addEventListener('message', handleMessage);

    // The tab closes itself right after sending this, so everything the
    // shopper sees from here on is painted by this frame.
    const handleTabPaymentAuthorized = ({ result, provider }: ApplePayTabPaymentAuthorizedMessage) => {
      if (paidInTabRef.current) return;
      paidInTabRef.current = true;
      logInfo('Apple Pay tab fallback: tab handed back an authorized payment', { orderId: result?.orderId });
      contextRef.current.actions.resumeApplePayTabPayment(result, provider);
    };

    let firstSeenClosedAt: number | null = null;

    const pollId = window.setInterval(() => {
      if (!tabRef.current?.closed) return;
      if (!paidInTabRef.current) {
        firstSeenClosedAt ??= Date.now();
        if (Date.now() - firstSeenClosedAt < HANDOFF_GRACE_AFTER_CLOSE_MS) return;
      }
      const paid = paidInTabRef.current;
      cleanup();
      mixpanelService.track(MixpanelEvents.APPLE_PAY_TAB_FALLBACK_TAB_CLOSED, {
        ...(getMixpanelProperties(contextRef.current) as Record<string, unknown>),
        paid_in_tab: paid,
      });
      // Paid: order polling and the success screen are already running here
      // (see `handleTabPaymentAuthorized`), and they own the loader until the
      // order resolves — clearing the payment state now would flash the bare
      // checkout over a paid order.
      if (paid) return;
      void settleTabClosedWithoutHandoff();
    }, TAB_CLOSED_POLL_INTERVAL_MS);

    /**
     * A tab that closed without handing anything back is almost always a
     * shopper who walked away. But it is also exactly what a tab that died
     * between Apple authorizing and the handoff being posted looks like — iOS
     * reclaiming a backgrounded tab, a crash, a message that never made it
     * out. No amount of message-protocol care covers that case, and nothing
     * in this window can tell the two apart, so ask the order itself before
     * putting a payable checkout back in front of a charged shopper.
     */
    const settleTabClosedWithoutHandoff = async () => {
      const { actions, orderId, paymentMethodsSettings } = contextRef.current;
      // The overlay names a window that no longer exists either way.
      actions.setApplePayTabOpen(false);

      if (orderId) {
        const order = await readOrder(orderId);
        if (order && ORDER_STATES_WITH_A_COMMITTED_PAYMENT.has(order.state)) {
          logInfo('Apple Pay tab fallback: tab closed silently on a committed order, resuming it here', {
            orderId,
            state: order.state,
          });
          actions.resumeApplePayTabPayment(
            { orderId } as BasePaymentResponse,
            getProviderFromPaymentMethod(PaymentMethodEnum.APPLE_PAY, paymentMethodsSettings)
          );
          return;
        }
        // Declined / failed in the tab: never resume as success — that path
        // opens the success screen, polls into charge_failed, and the fatal
        // error boundary closes the opener checkout. Restore a payable UI.
        if (order && ORDER_STATES_FAILED_ATTEMPT.has(order.state)) {
          logInfo('Apple Pay tab fallback: tab closed after a failed payment, restoring checkout', {
            orderId,
            state: order.state,
          });
          actions.setIsExpressPaymentInProcess(false);
          if (order.publisherErrorMessage) {
            actions.setError(order.publisherErrorMessage);
          }
          return;
        }
      }
      actions.setIsExpressPaymentInProcess(false);
    };

    const readOrder = async (orderId: string) => {
      try {
        return await getOrderRef.current(orderId);
      } catch (error) {
        // Unknowable rather than unpaid, but a checkout stuck on a spinner
        // forever is the worse of the two wrong answers, and the handoff
        // message already covers every case except a tab that died.
        logError('Apple Pay tab fallback: could not read the order after the tab closed', error);
        return null;
      }
    };

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(pollId);
      tabRef.current = null;
      cleanupRef.current = null;
    };
    cleanupRef.current = cleanup;

    return true;
  }, [queryClient]);

  return { openApplePayTab };
};
